/**
 * The person record's shared readings: which views a record holds, the address
 * of a view, and the labels and lifecycle shapes its views derive from the one
 * People detail response (ADR 0034: one person per human).
 */
import type { AccountListRow } from "@/lib/api/accounts";
import { ApiError } from "@/lib/api/client";
import type { EligibleFacultyAccount, FacultyProfile } from "@/lib/api/faculty";
import type {
  PeopleAccountLifecycleEvent,
  PeopleDetailResponse,
  PeopleMembership,
  PeopleRelationship,
  PeopleStudentRecord,
  PeopleStudentStatus,
  PersonKey,
} from "@/lib/api/people";
import { personName } from "@/lib/person-name";
import { registrarPersonPath, type RegistrarPersonView } from "@/navigation/routes";

export const RELATIONSHIP_LABEL: Record<PeopleRelationship, string> = {
  student: "Student",
  faculty: "Faculty",
  admin: "Admin",
  staff_no_profile: "Staff without profile",
};

const STATUS_LABEL: Record<PeopleStudentStatus, string> = {
  active: "Active",
  leave_of_absence: "Leave of absence",
  withdrawn: "Withdrawn",
  completed: "Completed",
};

export { NO_NAME } from "@/lib/person-name";

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

/**
 * A path segment is a person when it is a person id, or a retired key the
 * server still resolves (`user:{loginId}`, `student:{studentRecordId}`).
 */
export function isPersonKey(value: string): value is PersonKey {
  return new RegExp(`^${UUID}$`, "i").test(value) || /^(?:user|student):[^:]+$/.test(value);
}

/**
 * The views this record holds, in tab order. The record is always one; every
 * other view is offered only when the record carries what it shows.
 */
export function offeredPersonViews(detail: PeopleDetailResponse): RegistrarPersonView[] {
  const views: RegistrarPersonView[] = ["record"];
  if (detail.identity_and_access || detail.current_invitation) views.push("access");
  if (detail.student_record) views.push("student");
  if (detail.faculty_profile || detailEligibleFacultyAccount(detail) || detail.teaching_assignments) {
    views.push("teaching");
  }
  return views;
}

/** A view of a person, keeping every other parameter, in arrival order, and the hash. */
export function personViewHref(
  personKey: string,
  view: RegistrarPersonView,
  params: URLSearchParams,
  hash = "",
): string {
  const rest = new URLSearchParams(params);
  rest.delete("view");
  const path = registrarPersonPath(personKey, view);
  const query = rest.toString();
  return `${path}${query ? `${path.includes("?") ? "&" : "?"}${query}` : ""}${hash}`;
}

export function personDisplayName(detail: PeopleDetailResponse): string {
  return personName(detail.person.full_name);
}

export function relationshipsLabel(detail: PeopleDetailResponse, separator: string): string {
  return detail.relationships.map((relationship) => RELATIONSHIP_LABEL[relationship]).join(separator);
}

export function portalIdentityPostureLabel(
  posture: PeopleDetailResponse["portal_identity_posture"],
): string {
  return {
    internal_managed: "Internal managed account",
    internal_managed_incomplete: "Internal managed account - needs recovery",
    observed_existing_workspace_admin: "Existing Workspace administrator",
    legacy_unmanaged: "Legacy/unmanaged account",
    no_portal_account: "No portal account",
  }[posture];
}

export function statusLabel(status: PeopleStudentStatus | null): string | null {
  if (!status) return null;
  return STATUS_LABEL[status] ?? sentenceCase(status);
}

/** A stored value no word is mapped for, as words: "would_create" reads "Would create". */
export function sentenceCase(value: string): string {
  const words = value.replaceAll("_", " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const ENROLLMENT_STATUS_LABEL: Partial<Record<string, string>> = {
  enrolled: "Enrolled",
  withdrawn: "Withdrawn",
  incomplete: "Incomplete",
  complete: "Completed",
};

export function enrollmentStatusLabel(status: string): string {
  return ENROLLMENT_STATUS_LABEL[status] ?? sentenceCase(status);
}

const INVITATION_STATE_LABEL: Partial<Record<string, string>> = {
  provisioning: "Provisioning",
  failed: "Provisioning failed",
  issued: "Issued",
  claiming: "Activating",
  accepted: "Activated",
  revoked: "Revoked",
  superseded: "Replaced",
};

export function invitationStateLabel(state: string): string {
  return INVITATION_STATE_LABEL[state] ?? sentenceCase(state);
}

const ROLE_LABEL: Record<PeopleMembership["role"], string> = {
  admin: "Admin",
  instructor: "Instructor",
  student: "Student",
};

/** The LMS membership's role; a tenant super admin is named as one. */
export function membershipRoleLabel(membership: PeopleMembership | null): string {
  if (!membership) return "No LMS membership";
  if (membership.is_super_admin) return "Super admin";
  return ROLE_LABEL[membership.role] ?? sentenceCase(membership.role);
}

const LIFECYCLE_EVENT_LABEL: Record<PeopleAccountLifecycleEvent["event"], string> = {
  retired: "Retired",
  restore_requested: "Restore requested",
  restored: "Restored",
};

export function lifecycleEventLabel(event: PeopleAccountLifecycleEvent["event"]): string {
  return LIFECYCLE_EVENT_LABEL[event] ?? sentenceCase(event);
}

export function detailAccount(detail: PeopleDetailResponse): AccountListRow | null {
  const section = detail.identity_and_access;
  const membership = section?.membership;
  if (!section || !membership) return null;
  return {
    user_id: section.user_id,
    role: membership.role,
    is_active: membership.is_active,
    is_super_admin: membership.is_super_admin,
    created_at: membership.created_at,
    display_name: detail.person.full_name,
    email: detail.person.email,
    sid: detail.student_record?.sid ?? null,
    managed_state: detail.managed_state,
    observed_existing_workspace_admin: detail.observed_existing_workspace_admin,
    retirement: membership.retirement,
  };
}

/**
 * The student record this person is invited to the portal from (ADR 0036): an
 * active student record with no LMS account and no current invitation.
 */
export function invitableStudentId(detail: PeopleDetailResponse): string | null {
  const record = detail.student_record;
  if (record?.status !== "active" || detailAccount(detail) || detail.current_invitation) return null;
  return record.id;
}

export function detailEligibleFacultyAccount(detail: PeopleDetailResponse): EligibleFacultyAccount | null {
  const section = detail.identity_and_access;
  const membership = section?.membership;
  if (!section || !membership?.is_active || !["admin", "instructor"].includes(membership.role)) return null;
  if (detail.faculty_profile) return null;
  return {
    user_id: section.user_id,
    role: membership.role as "admin" | "instructor",
    is_active: true,
    is_super_admin: membership.is_super_admin,
    display_name: detail.person.full_name,
    email: detail.person.email,
  };
}

export function detailFacultyProfile(detail: PeopleDetailResponse): FacultyProfile | null {
  const profile = detail.faculty_profile;
  if (!profile) return null;
  const membership = detail.identity_and_access?.membership ?? null;
  return {
    ...profile,
    display_name: detail.person.full_name,
    account_role: membership?.role === "student" ? null : membership?.role ?? null,
    account_is_active: membership?.is_active ?? null,
    courses: detail.teaching_assignments?.courses ?? [],
  };
}

export function studentProfile(record: PeopleStudentRecord) {
  return {
    ...record,
    gpa_cached: record.gpa_cached === null ? null : Number(record.gpa_cached),
  };
}

export function apiMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "None recorded";
}

/**
 * A day, without the time: "Jul 21, 2026". A date-only `YYYY-MM-DD` is that
 * calendar date in every zone; `new Date` would read it as UTC midnight and
 * show the day before anywhere west of Greenwich. A timestamp is the viewer's
 * local day.
 */
export function formatDay(value: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
